import React, { useState, useRef } from 'react';
import { AlertTriangle, MapPin, Camera, Loader2, Search, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { STATUS_STYLES, CONDITION_COLORS, formatDate } from './constants';
import { authFetch, API } from '../../utils/api';


function compressImage(file, maxWidth = 800, quality = 0.65) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = Math.round((h * maxWidth) / w); w = maxWidth; }
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        let dataUrl = canvas.toDataURL('image/webp', quality);
        let mime = 'image/webp';
        if (dataUrl.length < 100) { dataUrl = canvas.toDataURL('image/jpeg', quality); mime = 'image/jpeg'; }
        const b64 = dataUrl.split(',')[1];
        resolve({ base64: b64, mime });
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function LostBoardsSection({ onNavigateToChat }) {
  const [lostBoards, setLostBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [foundLoading, setFoundLoading] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  const [matching, setMatching] = useState(false);
  const fileRef = useRef(null);

  const loadLost = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/boards/lost`);
      if (res.ok) { setLostBoards((await res.json()).boards || []); }
    } catch {} finally { setLoading(false); setLoaded(true); }
  };

  const handleFound = async (boardId) => {
    const location = prompt('Where did you find it? (spot/beach name)');
    if (!location) return;
    const notes = prompt('Any details? (optional)') || '';
    setFoundLoading(boardId);
    try {
      const res = await authFetch(`${API}/api/boards/${boardId}/found`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },         body: JSON.stringify({ location, notes }),
      });
      if (res.ok) {
        setLostBoards(prev => prev.filter(b => b.board_id !== boardId));
      }
    } catch {} finally { setFoundLoading(null); }
  };

  const handleMatchPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMatching(true);
    setMatchResult(null);
    try {
      const compressed = await compressImage(file);
      const res = await authFetch(`${API}/api/boards/match-photo`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },         body: JSON.stringify({ image: compressed.base64 }),
      });
      if (res.ok) { setMatchResult(await res.json()); }
    } catch {} finally { setMatching(false); }
  };

  if (!loaded) {
    return (
      <div className="bg-red-500/5 border border-red-500/15 rounded-2xl p-4" data-testid="lost-boards-section">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span className="text-xs font-semibold text-red-400">Community Lost & Found</span>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">See boards reported lost by the community. Found one? Help return it to its owner.</p>
        <button
          data-testid="load-lost-boards-btn"
          onClick={loadLost}
          className="text-[10px] px-4 py-1.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/15 transition-colors inline-flex items-center gap-1.5"
        >
          <Search className="w-3 h-3" /> View Lost Boards
        </button>
      </div>
    );
  }

  return (
    <div className="bg-red-500/5 border border-red-500/15 rounded-2xl p-4" data-testid="lost-boards-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span className="text-xs font-semibold text-red-400">Lost & Found</span>
          <span className="text-[9px] bg-red-500/10 text-red-400 px-1.5 py-0.5 rounded-full">{lostBoards.length}</span>
        </div>
        <button
          data-testid="refresh-lost-btn"
          onClick={loadLost}
          disabled={loading}
          className="text-[9px] text-muted-foreground/60 hover:text-white transition-colors"
        >
          {loading ? 'loading...' : 'refresh'}
        </button>
      </div>

      {/* Photo match tool */}
      <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 mb-3">
        <p className="text-[10px] text-muted-foreground mb-2">Found a board? Upload a photo to match it against lost boards.</p>
        <div className="flex items-center gap-2">
          <button
            data-testid="match-photo-btn"
            onClick={() => fileRef.current?.click()}
            disabled={matching}
            className={`text-[10px] px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${matching ? 'bg-white/5 text-muted-foreground/50' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/15'}`}
          >
            {matching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
            {matching ? 'Matching...' : 'Match Photo'}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleMatchPhoto} />
        </div>
        {matchResult && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2">
            {matchResult.matches && matchResult.matches.length > 0 ? (
              <div className="space-y-1.5">
                <p className="text-[10px] text-emerald-400 font-semibold">Potential matches found:</p>
                {matchResult.matches.map((m, i) => (
                  <div key={i} className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-white font-medium">{lostBoards.find(b => b.board_id === m.board_id)?.name || m.board_id}</span>
                      <span className="text-[9px] text-emerald-400">{Math.round(m.confidence * 100)}% match</span>
                    </div>
                    <p className="text-[9px] text-muted-foreground/60 mt-0.5">{m.reasoning}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-muted-foreground/60">{matchResult.no_match_summary || 'No matches found.'}</p>
            )}
          </motion.div>
        )}
      </div>

      {/* Lost boards list */}
      {lostBoards.length > 0 ? (
        <div className="space-y-2">
          {lostBoards.map((board) => {
            const isExpd = expanded === board.board_id;
            const condColor = CONDITION_COLORS[board.condition] || '';
            return (
              <div key={board.board_id} className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden" data-testid={`lost-board-${board.board_id}`}>
                <button
                  onClick={() => setExpanded(isExpd ? null : board.board_id)}
                  className="w-full flex items-center gap-3 p-3 text-left"
                >
                  <div className="w-8 h-8 bg-red-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{board.name}</p>
                    <p className="text-[10px] text-muted-foreground">{[board.board_type, board.length, board.brand].filter(Boolean).join(' · ')}</p>
                  </div>
                  {board.lost_spot && (
                    <div className="flex items-center gap-1 text-[9px] text-red-400/70 flex-shrink-0">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate max-w-[80px]">{board.lost_spot}</span>
                    </div>
                  )}
                  {isExpd ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground/40" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/40" />}
                </button>
                <AnimatePresence>
                  {isExpd && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                      <div className="px-3 pb-3 border-t border-white/5 pt-2 space-y-2">
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                          {board.brand && <div><span className="text-muted-foreground/50">Brand:</span> <span>{board.brand}</span></div>}
                          {board.length && <div><span className="text-muted-foreground/50">Length:</span> <span>{board.length}</span></div>}
                          {board.fins && <div><span className="text-muted-foreground/50">Fins:</span> <span>{board.fins}</span></div>}
                          {board.condition && <div><span className="text-muted-foreground/50">Condition:</span> <span className={condColor.split(' ')[0]}> {board.condition}</span></div>}
                          {board.lost_date && <div><span className="text-muted-foreground/50">Lost on:</span> <span className="text-red-400">{formatDate(board.lost_date)}</span></div>}
                          {board.lost_spot && <div><span className="text-muted-foreground/50">Lost at:</span> <span className="text-red-400">{board.lost_spot}</span></div>}
                        </div>
                        {board.owner_name && <p className="text-[10px] text-muted-foreground/50">Owner: <span className="text-white/70">{board.owner_name}</span></p>}
                        <p className="text-[9px] font-mono text-muted-foreground/30">{board.serial || board.board_id}</p>
                        <div className="flex gap-2">
                          <button
                            data-testid={`found-board-${board.board_id}`}
                            onClick={() => handleFound(board.board_id)}
                            disabled={foundLoading === board.board_id}
                            className="flex-1 text-[10px] px-3 py-2 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/15 transition-colors inline-flex items-center justify-center gap-1.5 font-semibold"
                          >
                            {foundLoading === board.board_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                            {foundLoading === board.board_id ? 'Reporting...' : 'I Found This Board'}
                          </button>
                          {onNavigateToChat && (
                            <button
                              data-testid={`chat-about-${board.board_id}`}
                              onClick={() => onNavigateToChat(board)}
                              className="text-[10px] px-3 py-2 rounded-xl bg-secondary/10 text-secondary border border-secondary/20 hover:bg-secondary/15 transition-colors inline-flex items-center gap-1.5"
                            >
                              <MessageCircle className="w-3 h-3" /> Chat with Swell
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground/50 text-center py-3">No lost boards reported. That's great news!</p>
      )}
    </div>
  );
}
